import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { BackButton, HomeButton } from '../components/NavButtons';
import axios from 'axios';
import {
  Megaphone, Plus, X, Check, Clock, CurrencyDollar, Image, VideoCamera,
  CheckCircle, XCircle, ArrowRight, Storefront, Upload
} from '@phosphor-icons/react';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const AD_PRICING = [
  { days: 7, price: 9.99, label: '1 Week', popular: false },
  { days: 14, price: 17.99, label: '2 Weeks', popular: true },
  { days: 30, price: 29.99, label: '1 Month', popular: false },
  { days: 90, price: 74.99, label: '3 Months', popular: false },
];

const POSITIONS = [
  { value: 'homepage', label: 'Homepage Banner' },
  { value: 'search_results', label: 'Search Results' },
  { value: 'listing_page', label: 'Listing Page' },
  { value: 'category_page', label: 'Category Page' },
];

const SellerAds = () => {
  const navigate = useNavigate();
  const { user, isSeller } = useAuth();
  const [searchParams] = useSearchParams();
  const [ads, setAds] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [listings, setListings] = useState([]);
  const [submitting, setSubmitting] = useState(false);
  const [uploading, setUploading] = useState(false);

  const [form, setForm] = useState({
    title: '', type: 'banner', media_url: '', link_url: '',
    listing_id: '', position: 'homepage', duration_days: 14,
    width: '', height: '',
  });

  useEffect(() => {
    if (user && isSeller) {
      fetchAds();
      fetchListings();
    }
  }, [user, isSeller]);

  // Handle payment success redirect
  useEffect(() => {
    const sessionId = searchParams.get('session_id');
    const adId = searchParams.get('ad_id');
    if (sessionId && adId) {
      verifyPayment(sessionId);
    }
    if (searchParams.get('cancelled') === 'true') {
      toast.error('Ad payment was cancelled');
    }
  }, [searchParams]);

  const fetchAds = async () => {
    try {
      const res = await axios.get(`${API_URL}/api/seller/ads`);
      setAds(res.data.ads || []);
    } catch (err) {
      console.error('Failed to fetch ads:', err);
    } finally {
      setLoading(false);
    }
  };

  const fetchListings = async () => {
    try {
      const res = await axios.get(`${API_URL}/api/listings/my-listings`);
      setListings(res.data.listings || []);
    } catch (err) {
      console.error('Failed to fetch listings:', err);
    }
  };

  const verifyPayment = async (sessionId) => {
    try {
      const res = await axios.get(`${API_URL}/api/seller/ads/payment-status/${sessionId}`);
      if (res.data.status === 'paid') {
        toast.success('Ad payment confirmed! Your ad is now live.');
        fetchAds();
      }
    } catch (err) {
      console.error('Payment verification failed:', err);
    }
  };

  const handleFileUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      const res = await axios.post(`${API_URL}/api/seller/upload-media`, formData, {
        
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      setForm(prev => ({ ...prev, media_url: res.data.media_url, type: res.data.media_type === 'video' ? 'video' : 'banner' }));
      toast.success('Media uploaded');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Upload failed');
    } finally {
      setUploading(false);
      e.target.value = '';
    }
  };

  const handleSubmit = async () => {
    if (!form.title.trim()) { toast.error('Please enter an ad title'); return; }
    if (!form.media_url.trim()) { toast.error('Please upload or enter media URL'); return; }

    setSubmitting(true);
    try {
      const res = await axios.post(`${API_URL}/api/seller/ads`, {
        ...form,
        origin_url: window.location.origin,
      });

      if (res.data.checkout_url) {
        window.location.href = res.data.checkout_url;
      } else {
        toast.success('Ad created');
        setShowForm(false);
        fetchAds();
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to create ad');
    } finally {
      setSubmitting(false);
    }
  };

  const handleDelete = async (adId) => {
    if (!window.confirm('Delete this ad?')) return;
    try {
      await axios.delete(`${API_URL}/api/seller/ads/${adId}`);
      toast.success('Ad deleted');
      fetchAds();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to delete');
    }
  };

  const selectedPlan = AD_PRICING.find(p => p.days === form.duration_days) || AD_PRICING[1];

  if (!user || !isSeller) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center p-4">
        <p className="text-slate-500">Please sign in as a seller to manage ads.</p>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-100">
      {/* Header */}
      <header className="sticky top-0 z-50 bg-white border-b border-slate-200 shadow-sm">
        <div className="max-w-5xl mx-auto px-4 h-14 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <BackButton />
            <HomeButton />
            <h1 className="font-semibold text-slate-900 text-lg flex items-center gap-2" data-testid="seller-ads-title">
              <Megaphone size={22} weight="fill" className="text-orange-500" /> Promote Your Spaces
            </h1>
          </div>
          {!showForm && (
            <Button onClick={() => setShowForm(true)} className="bg-orange-500 hover:bg-orange-600 rounded-xl h-9 text-sm"
              data-testid="create-ad-btn">
              <Plus size={16} className="mr-1.5" /> Create Ad
            </Button>
          )}
        </div>
      </header>

      <div className="max-w-5xl mx-auto px-4 py-6 space-y-6">
        {/* Create Ad Form */}
        {showForm && (
          <div className="bg-white rounded-xl border border-slate-200 overflow-hidden" data-testid="ad-create-form">
            <div className="px-4 py-3 bg-orange-50 border-b border-orange-100 flex items-center justify-between">
              <h2 className="text-sm font-semibold text-orange-800">Create New Ad Campaign</h2>
              <button onClick={() => setShowForm(false)} className="text-slate-400 hover:text-slate-600"><X size={18} /></button>
            </div>
            <div className="p-4 space-y-4">
              {/* Duration / Pricing Picker */}
              <div>
                <Label className="text-xs text-slate-500 mb-2 block">Select Duration & Pricing</Label>
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-2" data-testid="ad-pricing-grid">
                  {AD_PRICING.map(plan => (
                    <button key={plan.days}
                      onClick={() => setForm(prev => ({ ...prev, duration_days: plan.days }))}
                      className={`relative p-3 rounded-xl border-2 text-center transition-all ${form.duration_days === plan.days
                        ? 'border-orange-500 bg-orange-50'
                        : 'border-slate-200 bg-white hover:border-slate-300'}`}
                      data-testid={`plan-${plan.days}`}>
                      {plan.popular && (
                        <span className="absolute -top-2 left-1/2 -translate-x-1/2 text-[9px] bg-orange-500 text-white px-2 py-0.5 rounded-full font-bold">POPULAR</span>
                      )}
                      <p className="text-sm font-bold text-slate-800">{plan.label}</p>
                      <p className="text-lg font-black text-orange-600">${plan.price}</p>
                    </button>
                  ))}
                </div>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div>
                  <Label className="text-xs text-slate-500 mb-1 block">Ad Title *</Label>
                  <Input value={form.title} onChange={(e) => setForm(prev => ({ ...prev, title: e.target.value }))}
                    placeholder="e.g., Premium Parking Downtown" className="h-9 rounded-lg text-sm" data-testid="ad-title-input" />
                </div>
                <div>
                  <Label className="text-xs text-slate-500 mb-1 block">Placement</Label>
                  <select value={form.position} onChange={(e) => setForm(prev => ({ ...prev, position: e.target.value }))}
                    className="w-full h-9 px-2 border border-slate-200 rounded-lg text-sm bg-white focus:border-orange-400 outline-none"
                    data-testid="ad-position-select">
                    {POSITIONS.map(p => <option key={p.value} value={p.value}>{p.label}</option>)}
                  </select>
                </div>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div>
                  <Label className="text-xs text-slate-500 mb-1 block">Media Type</Label>
                  <select value={form.type} onChange={(e) => setForm(prev => ({ ...prev, type: e.target.value }))}
                    className="w-full h-9 px-2 border border-slate-200 rounded-lg text-sm bg-white focus:border-orange-400 outline-none"
                    data-testid="ad-type-select">
                    <option value="banner">Banner Image</option>
                    <option value="video">Video</option>
                  </select>
                </div>
                <div>
                  <Label className="text-xs text-slate-500 mb-1 block">Link to Listing (optional)</Label>
                  <select value={form.listing_id} onChange={(e) => setForm(prev => ({ ...prev, listing_id: e.target.value, link_url: e.target.value ? `/listing/${e.target.value}` : '' }))}
                    className="w-full h-9 px-2 border border-slate-200 rounded-lg text-sm bg-white focus:border-orange-400 outline-none"
                    data-testid="ad-listing-select">
                    <option value="">No specific listing</option>
                    {listings.map(l => <option key={l.listing_id} value={l.listing_id}>{l.title}</option>)}
                  </select>
                </div>
              </div>

              {/* Media Upload */}
              <div>
                <Label className="text-xs text-slate-500 mb-1 block">Media *</Label>
                <div className="flex gap-2 items-center">
                  <Input value={form.media_url} onChange={(e) => setForm(prev => ({ ...prev, media_url: e.target.value }))}
                    placeholder="Media URL or upload" className="h-9 rounded-lg text-sm flex-1" data-testid="ad-media-url-input" />
                  <label className={`flex items-center gap-1 px-3 h-9 rounded-lg border border-dashed border-slate-300 text-xs text-slate-500 hover:border-orange-400 hover:text-orange-600 cursor-pointer ${uploading ? 'opacity-50 pointer-events-none' : ''}`}
                    data-testid="ad-upload-btn">
                    <Upload size={14} />
                    {uploading ? 'Uploading...' : 'Upload'}
                    <input type="file" accept="image/*,video/*" onChange={handleFileUpload} className="hidden" />
                  </label>
                </div>
                {form.media_url && (
                  <div className="mt-2 p-2 bg-slate-50 rounded-lg">
                    {form.type === 'video' ? (
                      <video src={form.media_url.startsWith('/') ? `${API_URL}${form.media_url}` : form.media_url} className="max-h-32 rounded" controls />
                    ) : (
                      <img src={form.media_url.startsWith('/') ? `${API_URL}${form.media_url}` : form.media_url} alt="Preview" className="max-h-32 rounded object-contain" />
                    )}
                  </div>
                )}
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label className="text-xs text-slate-500 mb-1 block">Width (px)</Label>
                  <Input type="number" value={form.width} onChange={(e) => setForm(prev => ({ ...prev, width: e.target.value }))}
                    placeholder="Auto" className="h-9 rounded-lg text-sm" data-testid="ad-width-input" />
                </div>
                <div>
                  <Label className="text-xs text-slate-500 mb-1 block">Height (px)</Label>
                  <Input type="number" value={form.height} onChange={(e) => setForm(prev => ({ ...prev, height: e.target.value }))}
                    placeholder="Auto" className="h-9 rounded-lg text-sm" data-testid="ad-height-input" />
                </div>
              </div>

              {/* Submit */}
              <div className="flex items-center justify-between pt-2 border-t border-slate-100">
                <div className="text-sm text-slate-600">
                  Total: <span className="font-bold text-orange-600 text-lg">${selectedPlan.price}</span>
                  <span className="text-xs text-slate-400 ml-1">for {selectedPlan.label}</span>
                </div>
                <Button onClick={handleSubmit} disabled={submitting}
                  className="bg-orange-500 hover:bg-orange-600 rounded-xl h-10 px-6 font-semibold"
                  data-testid="submit-ad-btn">
                  {submitting ? 'Processing...' : (
                    <>Pay & Launch <ArrowRight size={16} className="ml-1.5" /></>
                  )}
                </Button>
              </div>
            </div>
          </div>
        )}

        {/* Existing Ads */}
        <div>
          <h2 className="text-base font-semibold text-slate-800 mb-3" data-testid="my-ads-title">
            My Ad Campaigns ({ads.length})
          </h2>

          {loading ? (
            <div className="space-y-3">
              {[...Array(2)].map((_, i) => (
                <div key={`ads-skeleton-${i}`} className="bg-white rounded-xl p-4 animate-pulse">
                  <div className="h-4 bg-slate-200 rounded w-1/3 mb-2"></div>
                  <div className="h-3 bg-slate-200 rounded w-1/2"></div>
                </div>
              ))}
            </div>
          ) : ads.length > 0 ? (
            <div className="space-y-3">
              {ads.map(ad => (
                <div key={ad.ad_id} className="bg-white rounded-xl border border-slate-200 p-4" data-testid={`ad-card-${ad.ad_id}`}>
                  <div className="flex items-start justify-between gap-3">
                    <div className="flex items-start gap-3 flex-1 min-w-0">
                      {ad.media_url && (
                        <div className="w-20 h-14 bg-slate-100 rounded-lg overflow-hidden shrink-0">
                          {ad.type === 'video' ? (
                            <video src={ad.media_url.startsWith('/') ? `${API_URL}${ad.media_url}` : ad.media_url} className="w-full h-full object-cover" />
                          ) : (
                            <img src={ad.media_url.startsWith('/') ? `${API_URL}${ad.media_url}` : ad.media_url} alt={ad.title} className="w-full h-full object-cover" />
                          )}
                        </div>
                      )}
                      <div className="flex-1 min-w-0">
                        <p className="font-semibold text-slate-900 text-sm truncate">{ad.title}</p>
                        <div className="flex items-center gap-2 mt-1 flex-wrap">
                          <span className={`text-[10px] px-2 py-0.5 rounded-full font-semibold ${
                            ad.payment_status === 'paid'
                              ? ad.active ? 'bg-emerald-100 text-emerald-700' : 'bg-slate-100 text-slate-600'
                              : 'bg-amber-100 text-amber-700'
                          }`}>
                            {ad.payment_status === 'paid' ? (ad.active ? 'Live' : 'Expired') : 'Pending Payment'}
                          </span>
                          <span className="text-[11px] text-slate-500">{ad.position?.replace('_', ' ')}</span>
                          <span className="text-[11px] text-slate-500">{ad.duration_days} days</span>
                          <span className="text-[11px] font-medium text-orange-600">${ad.price_paid}</span>
                        </div>
                        {ad.schedule_start && (
                          <p className="text-[11px] text-slate-400 mt-1 flex items-center gap-1">
                            <Clock size={11} />
                            {new Date(ad.schedule_start).toLocaleDateString()} - {new Date(ad.schedule_end).toLocaleDateString()}
                          </p>
                        )}
                      </div>
                    </div>
                    <div className="flex items-center gap-1 shrink-0">
                      {ad.payment_status !== 'paid' && (
                        <button onClick={() => handleDelete(ad.ad_id)}
                          className="p-1.5 text-red-400 hover:text-red-600 hover:bg-red-50 rounded-lg"
                          data-testid={`delete-ad-${ad.ad_id}`}>
                          <X size={16} />
                        </button>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="text-center py-16 bg-white rounded-xl border border-slate-200">
              <Megaphone size={48} className="text-slate-300 mx-auto mb-3" />
              <p className="text-slate-500 mb-1">No ad campaigns yet</p>
              <p className="text-xs text-slate-400 mb-4">Promote your listings to reach more buyers</p>
              <Button onClick={() => setShowForm(true)} className="bg-orange-500 hover:bg-orange-600 rounded-xl"
                data-testid="first-ad-btn">
                <Plus size={16} className="mr-1.5" /> Create Your First Ad
              </Button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default SellerAds;
